import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  LinkButton,
  PrimaryButton,
  SnackbarStatus,
  StatusBar,
  Typography,
} from '@weareopenr/component-library';
import { ButtonContainer } from 'src/components/ButtonContainer/ButtonContainer';
import { useModalNotification } from 'src/hooks/useModalNotification/useModalNotification';
import { useExternalSystems } from 'src/hooks/useExternalSystems';
import { useUpdateSectionAvailability } from 'src/hooks/useUpdateSectionAvailability/useUpdateSectionAvailability';
import { getChannelLabel, getPricingChannels } from 'src/helpers/channels/getPricingChannels';
import { resolveSectionAvailability } from 'src/helpers/sectionAvailability/sectionAvailability';
import { ISection } from 'src/types/menu.types';
import styles from './ManageAvailabilityTrayContent.module.scss';

export interface ManageAvailabilityTrayContentProps {
  menuId: string;
  section: ISection;
  sections: ISection[];
  onClose: () => void;
  openId: number;
}

export const ManageAvailabilityTrayContent = ({
  menuId,
  section,
  sections,
  onClose,
  openId,
}: ManageAvailabilityTrayContentProps): ReactElement => {
  const { errorMessage, showNotification, clearError } = useModalNotification();
  const { data: externalSystems, isLoading: isChannelsLoading } = useExternalSystems();
  const { mutateAsync: updateAvailability, isLoading: isSubmitting } = useUpdateSectionAvailability(
    menuId,
    section,
    onClose,
  );

  const [selectedInstanceIds, setSelectedInstanceIds] = useState<string[]>(section.instanceIds ?? []);

  useEffect(() => {
    setSelectedInstanceIds(section.instanceIds ?? []);
    clearError();
  }, [openId, section, clearError]);

  const parentSection = section.parentSectionId
    ? sections.find((candidate) => candidate.sectionId === section.parentSectionId)
    : undefined;

  const allowedInstanceIds = useMemo(
    () => (parentSection ? resolveSectionAvailability(parentSection, sections).instanceIds : null),
    [parentSection, sections],
  );

  const channelOptions = useMemo(() => {
    const channels = getPricingChannels(externalSystems);
    return channels.map((channel) => ({
      value: channel.extensionInstanceId,
      label: getChannelLabel(channel),
      disabled: !!allowedInstanceIds && !allowedInstanceIds.includes(channel.extensionInstanceId),
    }));
  }, [externalSystems, allowedInstanceIds]);

  const handleSave = async () => {
    clearError();
    try {
      await updateAvailability(selectedInstanceIds.length > 0 ? selectedInstanceIds : null);
    } catch (e) {
      showNotification({
        status: SnackbarStatus.ERROR,
        message:
          e instanceof Error && e.message
            ? e.message
            : 'Something went wrong whilst updating the menu section availability',
      });
    }
  };

  return (
    <div className={styles['content']}>
      <div className={styles['scroll-container']}>
        <Typography className={styles['subtitle']}>Define where this menu is published to.</Typography>

        {parentSection && allowedInstanceIds && (
          <div className={styles['inherited-notice']}>
            <StatusBar
              variant="info"
              message={`This sub-section can only be available on channels that "${parentSection.name}" is already available on.`}
            />
          </div>
        )}

        <div className={styles['multi-select-container']}>
          <Autocomplete
            multiple
            name="channels"
            label="Channels"
            options={channelOptions}
            value={selectedInstanceIds}
            onChange={setSelectedInstanceIds}
            loading={isChannelsLoading}
            disabled={isChannelsLoading}
            limitChips={1}
          />
        </div>
      </div>

      <div className={styles['button-row']}>
        <div className={styles['error-banner']}>
          {errorMessage && (
            <div className={styles['error-banner']}>
              <StatusBar message={errorMessage} variant="error" />
            </div>
          )}
        </div>
        <ButtonContainer
          direction="column-reverse"
          margin="none"
          left={
            <LinkButton type="button" onClick={onClose}>
              Close
            </LinkButton>
          }
          right={
            <PrimaryButton
              size="full-width"
              type="button"
              onClick={handleSave}
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Save
            </PrimaryButton>
          }
        />
      </div>
    </div>
  );
};

export default ManageAvailabilityTrayContent;
